import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Cookie, Shield } from 'lucide-react';
import { trackEvent } from '../utils/analytics';

const COOKIE_KEY = 'avi_cookie_consent';

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_KEY);
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 3000);
      return () => clearTimeout(timer);
    }
    // Restore previous consent on load
    try {
      const prev = JSON.parse(stored);
      if (prev.analytics && window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'granted',
          ad_storage: prev.marketing ? 'granted' : 'denied',
        });
      }
    } catch {}
  }, []);

  const handleAccept = (type) => {
    const consent = {
      essential: true,
      analytics: type === 'all',
      marketing: type === 'all',
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_KEY, JSON.stringify(consent));
    setVisible(false);

    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: type === 'all' ? 'granted' : 'denied',
        ad_storage: type === 'all' ? 'granted' : 'denied',
      });
    }

    trackEvent('cookie_consent', 'consent', {
      consent_type: type,
      analytics: type === 'all',
      marketing: type === 'all',
    });
  };

  const handleReject = () => {
    const consent = {
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_KEY, JSON.stringify(consent));
    setVisible(false);

    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
      });
    }

    trackEvent('cookie_consent', 'consent', {
      consent_type: 'rejected',
      analytics: false,
      marketing: false,
    });
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25 }}
          className="fixed bottom-0 left-0 right-0 z-[60] p-4 pb-16 sm:pb-4"
          data-testid="cookie-consent-banner"
        >
          <div className="container mx-auto max-w-4xl">
            <div className="bg-surface border border-white/10 rounded-2xl p-6 shadow-2xl shadow-black/40">
              {!showDetails ? (
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-10 h-10 rounded-xl bg-coral/10 flex items-center justify-center flex-shrink-0">
                      <Cookie className="w-5 h-5 text-coral" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-display font-medium mb-1">We use cookies</p>
                      <p className="text-gray-400 text-xs leading-relaxed">
                        We use cookies to improve your experience, analyze site traffic, and personalize content.{' '}
                        <button onClick={() => setShowDetails(true)} className="text-coral hover:underline" data-testid="cookie-details-btn">Learn more</button>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
                    <button
                      onClick={handleReject}
                      data-testid="cookie-reject-btn"
                      className="flex-1 sm:flex-none px-4 py-2 border border-white/10 hover:border-white/30 text-gray-300 hover:text-white text-sm font-display rounded-full transition-all"
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => handleAccept('all')}
                      data-testid="cookie-accept-btn"
                      className="flex-1 sm:flex-none px-5 py-2 bg-coral hover:bg-coral-dark text-white text-sm font-display font-medium rounded-full transition-all"
                    >
                      Accept All
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-lg font-semibold text-white flex items-center gap-2">
                      <Shield className="w-5 h-5 text-coral" /> Cookie Preferences
                    </h3>
                    <button onClick={() => setShowDetails(false)} className="text-gray-500 hover:text-white">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="space-y-3 mb-6">
                    <CookieRow title="Essential" description="Required for the website to function. Cannot be disabled." checked disabled />
                    <CookieRow title="Analytics" description="Google Analytics, Microsoft Clarity — helps us understand how visitors use our site." checked />
                    <CookieRow title="Marketing" description="Meta Pixel — used for personalized ads and remarketing." checked />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleAccept('essential')} className="flex-1 px-4 py-2 border border-white/10 hover:border-white/30 text-gray-300 text-sm font-display rounded-full transition-all" data-testid="cookie-essential-only">
                      Essential Only
                    </button>
                    <button onClick={() => handleAccept('all')} className="flex-1 px-5 py-2 bg-coral hover:bg-coral-dark text-white text-sm font-display font-medium rounded-full transition-all" data-testid="cookie-accept-all">
                      Accept All
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const CookieRow = ({ title, description, checked, disabled }) => (
  <div className="flex items-center justify-between bg-void/50 border border-white/5 rounded-xl p-4">
    <div>
      <p className="text-white text-sm font-medium">{title}</p>
      <p className="text-gray-500 text-xs">{description}</p>
    </div>
    <div className={`w-10 h-6 rounded-full flex items-center ${checked ? 'bg-coral justify-end' : 'bg-gray-700 justify-start'} ${disabled ? 'opacity-60' : 'cursor-pointer'} p-0.5 transition-all`}>
      <div className="w-5 h-5 rounded-full bg-white" />
    </div>
  </div>
);

export default CookieConsent;
